import { gql } from "@/graphql/typed-gql"

export const GET_AI_LOGS = gql`
  query GetAILogs($limit: Int, $offset: Int, $status: String) {
    aiLogs(limit: $limit, offset: $offset, status: $status) {
      logs {
        id
        sessionId
        studentId
        operation
        model
        promptTokens
        responseTokens
        durationMs
        status
        errorMessage
        keyType
        createdAt
      }
      total
      stats {
        totalCalls
        successfulCalls
        failedCalls
        avgDurationMs
        totalTokensUsed
      }
    }
  }
`

export const GET_AI_LOG_DETAIL = gql`
  query GetAILogDetail($id: ID!) {
    aiLog(id: $id) {
      id
      sessionId
      studentId
      operation
      model
      promptTokens
      responseTokens
      durationMs
      status
      errorMessage
      promptText
      responseText
      metadata
      createdAt
    }
  }
`

export const GET_EXERCISES_SUMMARY = gql`
  query GetExercisesSummary {
    exercisesSummary {
      summary
    }
  }
`

export const GET_EXERCISES_BY_SKILL = gql`
  query GetExercisesBySkill($subject: String!, $grade: Int!, $skill: String!, $level: Int!) {
    exercisesBySkill(subject: $subject, grade: $grade, skill: $skill, level: $level) {
      exercises {
        key
        title
        question {
          html
          answer
          hint
        }
        metadata {
          skillKey
          level
          exerciseKey
        }
        concepts
      }
      theory
      hasSeparateTheory
      concepts {
        key
        name
        description
        exerciseCount
        theoryBlocks
        target
        levels
        priority
        difficultyDeclared
        coverageMeasured
      }
    }
  }
`

export const GET_ALL_EXERCISES = gql`
  query GetAllExercises($subject: String!, $grade: Int, $skill: String) {
    allExercises(subject: $subject, grade: $grade, skill: $skill) {
      skills {
        key
        name
        grade
        levels {
          level
          exercises {
            key
            title
            question {
              html
              answer
              hint
            }
            metadata {
              skillKey
              level
              exerciseKey
            }
            concepts
          }
          concepts {
            key
            name
            description
            exerciseCount
            theoryBlocks
            target
            levels
            priority
            difficultyDeclared
            coverageMeasured
          }
        }
      }
      total
    }
  }
`

export const GET_PENDING_REVIEWS = gql`
  query GetPendingReviews($skillKey: String) {
    pendingReviews(skillKey: $skillKey)
  }
`

export const GET_EVALUATION_REVIEW = gql`
  query GetEvaluationReview($filePath: String!) {
    evaluationReview(filePath: $filePath)
  }
`

export const GET_PENDING_REVIEW_COUNTS = gql`
  query GetPendingReviewCounts {
    pendingReviewCounts
  }
`

export const GET_FLAGGED_CONTENT = gql`
  query GetFlaggedContent {
    flaggedContent
  }
`

export const GET_FLAG_COUNTS = gql`
  query GetFlagCounts {
    flagCounts
  }
`

export const GET_AVATAR_LIBRARY = gql`
  query GetAvatarLibrary {
    avatarLibrary {
      files
      pending
      pendingByStyle
    }
  }
`

export const GET_SETTINGS = gql`
  query GetSettings {
    settings {
      geminiMode
      chatAiProvider
      generationAiProvider
      evaluationAiProvider
      adjudicationAiProvider
      taggingAiProvider
      reportsAiProvider
      deepseekChatThinking
      aiCreditsMetered
      autoGuardianReports
      impersonationEnabled
      parentPinSet
      defaultExerciseCount
    }
  }
`

export const GENERATE_SVG_SHAPE = gql`
  query GenerateSvgShape($shapeType: String!, $paramsJson: String) {
    generateSvgShape(shapeType: $shapeType, paramsJson: $paramsJson) {
      svg
      shapeType
    }
  }
`

export const GET_SAVED_SVG_SHAPES = gql`
  query GetSavedSvgShapes {
    savedSvgShapes {
      svgPath
      yamlPath
      shapeType
      paramsJson
      svg
    }
  }
`

export const GET_SKILL_PROMPT = gql`
  query GetSkillPrompt($skillKey: String!, $subject: String!, $grade: Int!) {
    skillPrompt(skillKey: $skillKey, subject: $subject, grade: $grade)
  }
`

/**
 * Every subject, skill and concept the config declares — the options a content
 * filter offers.
 *
 * From `config/skills.yml` rather than from `exercisesSummary`, which is built by
 * walking `content/`: a skill nobody has generated for has no directory, so a
 * summary-derived picker cannot offer exactly the skills with the most work
 * outstanding.
 */
export const GET_CONTENT_CATALOG = gql`
  query GetContentCatalog {
    subjects {
      id
      name
      skills {
        id
        name
        grade
        concepts {
          key
          name
          levels
        }
      }
    }
  }
`

export const GET_THEORY_OVERVIEW = gql`
  query GetTheoryOverview($subjects: [String!], $grades: [Int!]) {
    theoryOverview(subjects: $subjects, grades: $grades) {
      skillKey
      skillName
      subject
      grade
      hasTheory
      tagged
      conceptCount
      questionCount
      untaggedQuestions
      unexplainedConcepts
      conceptsWithoutExample
      unpractisedConcepts
      conceptsWithoutIntro
    }
  }
`

export const GET_THEORY_PREVIEW = gql`
  query GetTheoryPreview($subject: String!, $grade: Int!, $skill: String!) {
    theoryPreview(subject: $subject, grade: $grade, skill: $skill) {
      subject
      grade
      skillKey
      skillName
      theory
      hasTheory
      tagged
      concepts {
        key
        name
        description
        theoryBlocks
        hasExample
        hasIntro
        exerciseTotal
        exerciseCounts {
          level
          count
        }
      }
      levels {
        level
        questions
        tagged
        untagged
      }
    }
  }
`

export const GET_PROMPT_LAYERS = gql`
  query GetPromptLayers(
    $subject: String
    $grade: Int
    $level: Int
    $skillKey: String
    $count: Int
    $conceptKey: String
  ) {
    promptLayers(
      subject: $subject
      grade: $grade
      level: $level
      skillKey: $skillKey
      count: $count
      conceptKey: $conceptKey
    )
  }
`

export const GET_THEORY_PROMPT_LAYERS = gql`
  query GetTheoryPromptLayers($subject: String, $grade: Int, $skillKey: String) {
    theoryPromptLayers(subject: $subject, grade: $grade, skillKey: $skillKey)
  }
`

export const GET_SHAPE_PACKS = gql`
  query GetShapePacks {
    shapePacks
  }
`

/**
 * One page of the review queue. Filtering, sorting and paging all happen on the
 * server: the queue is ~8,000 drafts and nobody reviews 8,000 questions in one
 * sitting, so shipping every one of them their question HTML was ~4 MB and
 * 8,000 cards to show the handful a reviewer can read.
 *
 * A page is whole skills, never a row count — „Patvirtinti visus" on a skill
 * approves that skill's entire pending set, so half a skill on screen would be a
 * button doing more than the reviewer can see.
 *
 * `atSkill` is the only way this page moves, and the resolver's `page` is
 * deliberately not declared here: two ways to say which page to open is one that
 * can be sent and silently ignored, which is exactly what happened when this
 * document still said `$page` while the component had moved on to `atSkill`.
 */
export const GET_PENDING_EXERCISES = gql`
  query GetPendingExercises(
    $subjects: [String!]
    $grades: [Int!]
    $skills: [String!]
    $levels: [Int!]
    $concepts: [String!]
    $sort: String
    $atSkill: String
  ) {
    pendingExercises(
      subjects: $subjects
      grades: $grades
      skills: $skills
      levels: $levels
      concepts: $concepts
      sort: $sort
      atSkill: $atSkill
    )
  }
`

/**
 * Every draft matching the filters, as paths alone — what „Ištrinti visą sąrašą"
 * acts on, the one action that reaches past the skill on screen. Fetched only
 * when it is pressed: half a megabyte of paths on every page load would be
 * several times the page's own content.
 */
export const GET_PENDING_EXERCISE_PATHS = gql`
  query GetPendingExercisePaths(
    $subjects: [String!]
    $grades: [Int!]
    $skills: [String!]
    $levels: [Int!]
    $concepts: [String!]
  ) {
    pendingExercisePaths(
      subjects: $subjects
      grades: $grades
      skills: $skills
      levels: $levels
      concepts: $concepts
    )
  }
`

export const GET_PENDING_EXERCISE_COUNTS = gql`
  query GetPendingExerciseCounts {
    pendingExerciseCounts
  }
`

export const GET_PENDING_EXERCISE_PREVIEWS = gql`
  query GetPendingExercisePreviews($skillKey: String!, $level: Int) {
    pendingExercisePreviews(skillKey: $skillKey, level: $level) {
      filePath
      problemHtml
      answer
      choices
      hint
    }
  }
`

/**
 * Every account, grown-ups included — the learners list deliberately excludes
 * them, and user management is about the people who sign in. Admin-only on the
 * server.
 */
export const GET_ACCOUNTS = gql`
  query Accounts {
    accounts {
      id
      name
      email
      role
      subscribed
      subscribedFrom
      subscribedUntil
      subscriptionHolderId
      aiCredits
      monthlyAiCredits
      metered
    }
  }
`
